import classNames from "classnames";
import { AgentCallingModals } from "components/chat/CallAgentModals";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Loader from "components/DesignSystem/Loader/loader";
import { BooksServicesSubscriptionsModal } from "components/product/BooksServicesSubscriptionsModal";
import { useLeftBar } from "components/Sidebar/LeftBar";
import { HelpContainer } from "components/TopBar/HelpContainer";
import { CaretRight } from "components/icons/CaretRight";
import { HelpLibraryIcon } from "components/icons/HelpLibraryIcon";
import { CALL_US_CLICKED } from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { useModal } from "hooks/useModal";
import { useState } from "react";
import { useCheckAgentsAvailabilityQuery } from "store/apis/agents";
import { useGetEntitiesQuery } from "store/apis/group";

export const GetHelpNavItem = ({ entityId }: { entityId?: string }) => {
  const { open: navOpen } = useLeftBar();
  const isSmallScreen = useIsSmallScreen();
  const { trackEvent } = useAnalytics();
  const agentCallingModals = useModal();
  const serviceModal = useModal();
  const [modalType, setModalType] = useState<string>("Call Inkle Support");
  const [open, setOpen] = useState(false);

  const group = useCurrentGroupContext();

  const { data: availableAgents, isLoading } = useCheckAgentsAvailabilityQuery(
    { groupId: group.uuid },
    { skip: !group.uuid }
  );

  const { data: { groups: [mygroup] = [null] } = {} } = useGetEntitiesQuery();
  const agentId = mygroup?.assigned_agent?.agent_id;

  const isSupportTeamAgentAvaliable =
    (availableAgents?.available_agent_count ?? 0) > 0;

  const rowClass = classNames(
    "all:unset t-box-border t-relative t-my-0.5 t-flex t-h-9 t-w-full t-cursor-pointer t-items-center t-gap-2 t-overflow-hidden t-rounded-md t-transition-all hover:t-bg-neutral-0",
    {
      "t-px-3": navOpen,
      "t-justify-center t-px-0": !navOpen,
    }
  );

  return (
    <>
      <Dropdown.Root
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (isOpen) trackEvent(CALL_US_CLICKED);
        }}
      >
        <Dropdown.Trigger asChild>
          <div role="button" className={rowClass}>
            <span className="t-flex t-h-5 t-w-5 t-shrink-0 t-items-center t-justify-center t-text-text-30">
              <HelpLibraryIcon />
            </span>
            {navOpen && (
              <div className="t-flex t-min-w-0 t-flex-1 t-items-center t-justify-between t-gap-2">
                <span className="t-whitespace-nowrap t-text-body-sm t-text-text-60">
                  Get help
                </span>
                <span className="t-flex t-text-text-30">
                  <CaretRight className="t-size-3.5" />
                </span>
              </div>
            )}
          </div>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content
            sideOffset={12}
            align={isSmallScreen ? "start" : "end"}
            side={isSmallScreen ? "top" : "right"}
            className={classNames(
              "!t-rounded-lg t-border t-border-solid t-border-neutral-0 t-bg-white t-shadow-box-shadow t-p-4",
              isSmallScreen ? "t-w-[calc(100vw-24px)]" : "t-w-[460px]"
            )}
          >
            <Async.Root
              isLoading={isLoading}
              isSuccess={true}
              customLoader={
                <div className="t-flex t-items-center t-gap-2 t-px-3 t-py-1 t-text-neutral-40">
                  <Loader customType="secondary" size="small" />
                  <div className="t-text-body-sm t-leading-[12px]">
                    Loading...
                  </div>
                </div>
              }
              isEmpty={false}
            >
              <Async.Success>
                <HelpContainer
                  setOpen={setOpen}
                  agentCallingModals={agentCallingModals}
                  setModalType={setModalType}
                  serviceModal={serviceModal}
                  isSupportTeamAgentAvaliable={isSupportTeamAgentAvaliable}
                  entityIdFromParent={entityId}
                />
              </Async.Success>
            </Async.Root>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>

      <AgentCallingModals
        close={agentCallingModals.close}
        isOpen={agentCallingModals.isOpen}
        type={modalType}
        agentId={agentId || ""}
      />
      <BooksServicesSubscriptionsModal
        open={serviceModal.isOpen}
        onClose={serviceModal.close}
        hideAnualPlan={false}
        description={
          <div className="t-text-subtext t-text-text-60 t-pl-1">
            Unlock access to bookkeeping expert and more with Books Service Plan
          </div>
        }
      />
    </>
  );
};
